/** 비교 이력 컴포넌트 */

import { useQuery } from "@tanstack/react-query";
import queries from "@/queries";
import { Clock, FileText } from "lucide-react";

interface ComparisonHistoryProps {
  onSelect: (comparisonId: string) => void;
}

export const ComparisonHistory = ({ onSelect }: ComparisonHistoryProps) => {
  const {
    data: history,
    isLoading,
    isError,
  } = useQuery({
    ...queries.comparisonHistory(5, 0),
    staleTime: 60 * 1000,
  });

  const formatDate = (dateStr: string) => {
    // 서버에서 UTC로 내려오므로 Z suffix가 없으면 명시적으로 추가
    const normalized =
      dateStr.endsWith("Z") || dateStr.includes("+") ? dateStr : `${dateStr}Z`;
    const date = new Date(normalized);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffMin < 1) return "방금 전";
    if (diffMin < 60) return `${diffMin}분 전`;
    if (diffHour < 24) return `${diffHour}시간 전`;
    if (diffDay < 7) return `${diffDay}일 전`;
    return date.toLocaleDateString("ko-KR", {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="w-full max-w-4xl mx-auto mt-10">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-4 h-4 text-neutral-400" />
        <h3 className="label-1-normal-medium text-neutral-600">
          최근 비교 이력
        </h3>
      </div>
      <div className="bg-white rounded-xl border border-neutral-200 divide-y divide-neutral-100 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-neutral-400">
            <div className="w-5 h-5 border-2 border-neutral-300 border-t-indigo-500 rounded-full animate-spin" />
          </div>
        ) : isError ? (
          <div className="py-8 text-center caption-1-medium text-neutral-400">
            비교 이력을 불러오지 못했습니다
          </div>
        ) : !history || history.length === 0 ? (
          <div className="py-8 text-center caption-1-medium text-neutral-400">
            비교 이력이 없습니다
          </div>
        ) : (
          history.map((item) => (
            <button
              key={item.comparisonId}
              onClick={() => onSelect(item.comparisonId)}
              className="w-full flex items-center gap-4 px-5 py-3.5 text-left hover:bg-neutral-50 transition-colors"
            >
              <FileText className="w-4 h-4 text-neutral-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="caption-1-medium text-neutral-800 truncate">
                    {item.dsdFilename || "DSD"}
                  </span>
                  <span className="caption-2-medium text-neutral-400">vs</span>
                  <span className="caption-1-medium text-neutral-800 truncate">
                    {item.ixdFilename || "IXD"}
                  </span>
                </div>
              </div>
              <span className="caption-2-medium text-neutral-400 shrink-0">
                {formatDate(item.createdAt)}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
};
